import logo from '@/assets/logo.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analytics } from '@/lib/analytics';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { InputWithError } from './InputWithError';

export const ActivateForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useTranslation('auth');

  const token = searchParams.get('token');

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    validate: (values) => {
      const errors: {
        firstName?: string;
        lastName?: string;
        password?: string;
        confirmPassword?: string;
      } = {};

      if (!values.firstName.trim()) {
        errors.firstName = t('activate.firstNameRequired');
      }

      if (!values.lastName.trim()) {
        errors.lastName = t('activate.lastNameRequired');
      }

      if (!values.password) {
        errors.password = t('activate.passwordRequired');
      } else if (values.password.length < 8) {
        errors.password = t('activate.passwordMinLength');
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = t('activate.confirmPasswordRequired');
      } else if (values.password !== values.confirmPassword) {
        errors.confirmPassword = t('activate.passwordsNoMatch');
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (!token) {
        toast.error(t('activate.invalidActivationToast'), {
          description: t('activate.invalidActivationToastDescription'),
        });
        return;
      }

      setIsLoading(true);

      const toastId = toast.loading(t('activate.activatingAccount'), {
        description: t('activate.activatingDescription'),
      });

      try {
        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/activate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            password: values.password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t('activate.failedActivate'));
        }

        if (data.success && data.token) {
          analytics.capture('Account Activated');
          // Store the token and redirect to dashboard
          localStorage.setItem('@actegon.gis', data.token);
          setIsSubmitted(true);
          toast.success(t('activate.activatedSuccessfully'), {
            description: t('activate.welcomeToGis'),
            id: toastId,
          });
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/');
          }, 2000);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('activate.failedActivate');
        toast.error(t('activate.failedActivate'), {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (!token) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className='overflow-hidden p-0'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <div className='p-6 md:p-8'>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col items-center text-center'>
                  <img src={logo} alt='Logo' className='size-16 mb-6' />
                  <h1 className='text-2xl font-bold'>{t('activate.invalidActivationLink')}</h1>
                  <p className='text-muted-foreground text-balance'>{t('activate.invalidActivationDescription')}</p>
                </div>
                <div className='flex flex-col gap-4'>
                  <Button asChild variant='ghost' className='w-full'>
                    <Link to='/auth/login'>{t('activate.backToLogin')}</Link>
                  </Button>
                </div>
              </div>
            </div>
            <div className='bg-muted relative hidden md:block'>
              <img
                src='/placeholder.svg'
                alt='Image'
                className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className='overflow-hidden p-0'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <div className='p-6 md:p-8'>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col items-center text-center'>
                  <img src={logo} alt='Logo' className='size-16 mb-6' />
                  <h1 className='text-2xl font-bold'>{t('activate.accountActivated')}</h1>
                  <p className='text-muted-foreground text-balance'>{t('activate.accountActivatedDescription')}</p>
                </div>
                <div className='flex flex-col gap-4'>
                  <Button asChild className='w-full'>
                    <Link to='/'>{t('activate.goToDashboard')}</Link>
                  </Button>
                </div>
              </div>
            </div>
            <div className='bg-muted relative hidden md:block'>
              <img
                src='/placeholder.svg'
                alt='Image'
                className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <img src={logo} alt='Logo' className='size-16 mb-6' />
                <h1 className='text-2xl font-bold'>{t('activate.title')}</h1>
                <p className='text-muted-foreground text-balance'>{t('activate.description')}</p>
              </div>
              <InputWithError
                label={t('activate.firstName')}
                id='firstName'
                name='firstName'
                type='text'
                placeholder={t('activate.firstNamePlaceholder')}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName ? formik.errors.firstName : undefined}
                touched={formik.touched.firstName}
                required
              />
              <InputWithError
                label={t('activate.lastName')}
                id='lastName'
                name='lastName'
                type='text'
                placeholder={t('activate.lastNamePlaceholder')}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName ? formik.errors.lastName : undefined}
                touched={formik.touched.lastName}
                required
              />
              <InputWithError
                label={t('activate.password')}
                id='password'
                name='password'
                type='password'
                placeholder={t('activate.passwordPlaceholder')}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password ? formik.errors.password : undefined}
                touched={formik.touched.password}
                required
              />
              <InputWithError
                label={t('activate.confirmPassword')}
                id='confirmPassword'
                name='confirmPassword'
                type='password'
                placeholder={t('activate.confirmPasswordPlaceholder')}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined}
                touched={formik.touched.confirmPassword}
                required
              />
              <div className='flex flex-col gap-2'>
                <Button type='submit' className='w-full' disabled={isLoading}>
                  {isLoading ? t('activate.activating') : t('activate.activateAccount')}
                </Button>
                <div className='text-center'>
                  <Button asChild variant='link' className='text-sm'>
                    <Link to='/auth/login'>{t('activate.backToLogin')}</Link>
                  </Button>
                </div>
              </div>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block'>
            <img
              src='/placeholder.svg'
              alt='Image'
              className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
            />
          </div>
        </CardContent>
      </Card>
      <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
        By clicking continue, you agree to our <a href='https://skygis.cloud/terms'>{t('login.termsOfService')}</a> and{' '}
        <a href='https://skygis.cloud/terms'>{t('login.privacyPolicy')}</a>.
      </div>
    </div>
  );
};
